// Phase 10 - AI Study Assistant, Notes/Summary/Flashcards/Quiz Generators,
// Faculty AI Question Generator + Review, Admin AI Dashboard, Compliance
// Insights, Predictive Risk, Recommendations.
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, Bot, Send, MessageSquare, BookOpen, FileText, ClipboardList,
  Lightbulb, ShieldCheck, TrendingUp, AlertTriangle, Wand2, Loader2,
  CircleCheckBig, XCircle, ThumbsUp, ThumbsDown, RefreshCw, Trash2,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// ============================================================
// SHARED HELPERS
// ============================================================

function AIBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#101876] to-[#329112] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
      <Sparkles className="h-3 w-3" /> AI
    </span>
  );
}

function ChatBubble({ role, text }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        data-testid={`chat-bubble-${role}`}
        className={`max-w-[75%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${
          isUser
            ? "bg-[#101876] text-white"
            : "bg-white border border-slate-200 text-slate-800"
        }`}
      >
        {!isUser && (
          <div className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#329112]">
            <Bot className="h-3 w-3" /> AI Assistant
          </div>
        )}
        {text}
      </div>
    </div>
  );
}

function useChat(endpoint, extraBody = {}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const boxRef = useRef();

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const q = input.trim();
    if (!q || sending) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post(endpoint, {
        message: q, session_id: sessionId, ...extraBody,
      });
      const r = data.data || {};
      if (r.session_id && !sessionId) setSessionId(r.session_id);
      setMessages((m) => [...m, { role: "assistant", text: r.reply || "" }]);
    } catch (err) {
      toast.error(formatApiError(err));
      setMessages((m) => [...m, { role: "assistant", text: "Error: could not reach AI. Please retry." }]);
    } finally {
      setSending(false);
    }
  };

  const clear = () => { setMessages([]); setSessionId(null); };

  return { messages, input, setInput, sending, send, clear, boxRef, sessionId };
}

// ============================================================
// LEARNER — AI STUDY ASSISTANT
// ============================================================

export function LearnerAIStudyAssistantPage() {
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState("");
  const chat = useChat("/learner/ai/study-assistant/chat", { course_id: courseId || null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/learner/my-courses");
        const list = (data.data?.mandatory || []).concat(data.data?.optional || []);
        setCourses(list);
      } catch { /* ignore */ }
    })();
  }, []);

  return (
    <DashboardLayout title="AI Study Assistant" breadcrumbs={["My Learning", "AI Assistant"]}>
      <div className="grid gap-4 lg:grid-cols-[280px,1fr]" data-testid="ai-study-assistant">
        <aside className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
          <div className="flex items-center gap-2">
            <AIBadge />
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Ask about</span>
          </div>
          <Select value={courseId || "any"} onValueChange={(v) => setCourseId(v === "any" ? "" : v)}>
            <SelectTrigger data-testid="ai-course-select"><SelectValue placeholder="Course" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any assigned course</SelectItem>
              {courses.map((c) => (
                <SelectItem key={c.course_id} value={c.course_id}>{c.course_title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="rounded-lg bg-slate-50 p-3 text-[11px] leading-relaxed text-slate-600">
            Ask questions related to your <strong>assigned</strong> courses. AI is disabled during active exams.
          </div>
          <button
            type="button" onClick={chat.clear} data-testid="ai-clear-chat"
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Clear chat
          </button>
        </aside>
        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 flex flex-col h-[70vh]">
          <div ref={chat.boxRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {chat.messages.length === 0 && (
              <div className="text-center text-sm text-slate-500 py-8">
                <Bot className="mx-auto h-8 w-8 mb-2 text-[#101876]" />
                Ask your first question. Try: <em>&ldquo;Explain hand hygiene in simple language&rdquo;</em>.
              </div>
            )}
            {chat.messages.map((m, i) => (
              <ChatBubble key={i} role={m.role} text={m.text} />
            ))}
            {chat.sending && (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> AI is thinking…
              </div>
            )}
          </div>
          <div className="border-t border-slate-200 bg-white p-3 flex gap-2">
            <input
              type="text" value={chat.input}
              onChange={(e) => chat.setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); chat.send(); } }}
              placeholder="Ask about your course…"
              data-testid="ai-chat-input"
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#101876]/30"
            />
            <button
              type="button" onClick={chat.send} disabled={chat.sending}
              data-testid="ai-chat-send"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-[#0b1258]"
            >
              <Send className="h-4 w-4" /> Send
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// LEARNER — AI NOTES / SUMMARY / FLASHCARDS / QUIZ
// ============================================================

export function LearnerAIStudioPage() {
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [tab, setTab] = useState("notes");
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState(null);
  const [count, setCount] = useState(5);
  const [difficulty, setDifficulty] = useState("medium");
  const [noteType, setNoteType] = useState("short_notes");
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/learner/my-courses");
        const list = (data.data?.mandatory || []).concat(data.data?.optional || []);
        setCourses(list);
        if (list.length > 0) setCourseId(list[0].course_id);
      } catch { /* ignore */ }
    })();
  }, []);

  const generate = async () => {
    if (!courseId) return toast.error("Select a course first");
    setLoading(true); setOutput(null); setQuizAnswers({}); setQuizResult(null);
    try {
      let ep = "/learner/ai/generate-notes";
      const body = { course_id: courseId };
      if (tab === "notes") { body.note_type = noteType; }
      if (tab === "summary") { ep = "/learner/ai/generate-summary"; body.scope = "course"; }
      if (tab === "flashcards") { ep = "/learner/ai/generate-flashcards"; body.count = count; body.difficulty = difficulty; }
      if (tab === "quiz") { ep = "/learner/ai/generate-practice-quiz"; body.count = count; body.difficulty = difficulty; }
      const { data } = await api.post(ep, body);
      setOutput({ tab, data: data.data });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const submitQuiz = async () => {
    if (!output?.data?.quiz_id) return;
    const answers = output.data.questions.map((_, i) => quizAnswers[i] ?? -1);
    try {
      const { data } = await api.post("/learner/ai/submit-practice-quiz", { quiz_id: output.data.quiz_id, answers });
      setQuizResult(data.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardLayout title="AI Learning Studio" breadcrumbs={["My Learning", "AI Studio"]}>
      <div className="space-y-4" data-testid="ai-studio">
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4">
          <AIBadge />
          <div className="flex-1 min-w-[240px]">
            <Select value={courseId} onValueChange={setCourseId}>
              <SelectTrigger data-testid="ai-studio-course"><SelectValue placeholder="Course" /></SelectTrigger>
              <SelectContent>
                {courses.map((c) => (
                  <SelectItem key={c.course_id} value={c.course_id}>{c.course_title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1">
            {[
              ["notes", "Notes", FileText],
              ["summary", "Summary", BookOpen],
              ["flashcards", "Flashcards", Wand2],
              ["quiz", "Practice Quiz", Lightbulb],
            ].map(([k, label, Icon]) => (
              <button
                key={k} type="button" onClick={() => { setTab(k); setOutput(null); setQuizResult(null); }}
                data-testid={`ai-studio-tab-${k}`}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold ${
                  tab === k ? "bg-[#101876] text-white" : "text-slate-600 hover:bg-white"
                }`}
              ><Icon className="h-3.5 w-3.5" /> {label}</button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-3">
          {tab === "notes" && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Note style</label>
              <Select value={noteType} onValueChange={setNoteType}>
                <SelectTrigger className="w-44" data-testid="ai-note-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="short_notes">Short notes</SelectItem>
                  <SelectItem value="detailed">Detailed</SelectItem>
                  <SelectItem value="bullets">Bullet points</SelectItem>
                  <SelectItem value="key_terms">Key terms</SelectItem>
                  <SelectItem value="revision">Revision</SelectItem>
                  <SelectItem value="exam_prep">Exam prep</SelectItem>
                  <SelectItem value="checklist">Practical checklist</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {(tab === "flashcards" || tab === "quiz") && (
            <>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Count</label>
                <input
                  type="number" min={3} max={15} value={count} onChange={(e) => setCount(parseInt(e.target.value) || 5)}
                  data-testid="ai-count-input"
                  className="w-20 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Difficulty</label>
                <Select value={difficulty} onValueChange={setDifficulty}>
                  <SelectTrigger className="w-32" data-testid="ai-difficulty"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <div className="flex-1"></div>
          <button
            type="button" onClick={generate} disabled={loading || !courseId}
            data-testid="ai-generate-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#101876] to-[#329112] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Generate
          </button>
        </div>

        {loading && <LoadingBlock />}
        {output && output.tab === "notes" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="ai-notes-output">
            <div className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-500">Generated Notes</div>
            <pre className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700 font-sans">{output.data.note.generated_content}</pre>
          </div>
        )}
        {output && output.tab === "summary" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="ai-summary-output">
            <div className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-500">Generated Summary</div>
            <pre className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700 font-sans">{output.data.summary}</pre>
          </div>
        )}
        {output && output.tab === "flashcards" && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="ai-flashcards-output">
            {output.data.flashcards.map((f, i) => (
              <Flashcard key={f.id} card={f} index={i} />
            ))}
          </div>
        )}
        {output && output.tab === "quiz" && (
          <div className="space-y-3" data-testid="ai-quiz-output">
            {output.data.questions.map((q, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-sm font-semibold text-slate-800">
                  Q{i + 1}. {q.question}
                </div>
                <div className="mt-3 space-y-2">
                  {q.options?.map((opt, oi) => {
                    const chosen = quizAnswers[i] === oi;
                    const submitted = !!quizResult;
                    const isCorrect = submitted && quizResult.review[i].correct_index === oi;
                    const isWrong = submitted && chosen && !isCorrect;
                    return (
                      <button
                        key={oi} type="button"
                        onClick={() => !submitted && setQuizAnswers({ ...quizAnswers, [i]: oi })}
                        data-testid={`ai-quiz-opt-${i}-${oi}`}
                        className={`w-full text-left rounded-lg border px-3 py-2 text-sm ${
                          submitted ? (isCorrect ? "border-green-500 bg-green-50" : isWrong ? "border-rose-500 bg-rose-50" : "border-slate-200")
                                    : (chosen ? "border-[#101876] bg-[#101876]/5" : "border-slate-200 hover:bg-slate-50")
                        }`}
                      >{opt}</button>
                    );
                  })}
                </div>
                {quizResult && (
                  <div className="mt-2 rounded-lg bg-slate-50 p-2 text-xs text-slate-600">
                    <strong>Explanation:</strong> {q.explanation}
                  </div>
                )}
              </div>
            ))}
            {!quizResult && (
              <button
                type="button" onClick={submitQuiz} data-testid="ai-quiz-submit"
                className="rounded-lg bg-[#329112] px-4 py-2 text-sm font-semibold text-white"
              >Submit answers</button>
            )}
            {quizResult && (
              <div className="rounded-2xl border-2 border-[#329112] bg-green-50 p-4" data-testid="ai-quiz-result">
                <div className="text-lg font-bold text-[#101876]">
                  Score: {quizResult.correct}/{quizResult.total} ({quizResult.score_percentage}%)
                </div>
                {quizResult.weak_topics?.length > 0 && (
                  <div className="mt-2 text-xs text-slate-600">
                    <strong>Weak topics:</strong> {quizResult.weak_topics.map((w) => w.topic).join(", ")}
                  </div>
                )}
                <div className="mt-2 text-[11px] text-slate-500">Practice quiz — does not affect official exam records.</div>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Flashcard({ card }) {
  const [flip, setFlip] = useState(false);
  return (
    <button
      type="button" onClick={() => setFlip((f) => !f)}
      data-testid={`ai-flashcard-${card.id}`}
      className="min-h-[140px] rounded-2xl border border-slate-200 bg-white p-4 text-left hover:shadow-md transition-shadow"
    >
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
        {flip ? "Back" : "Front"}
      </div>
      <div className="mt-2 text-sm text-slate-800 whitespace-pre-wrap">
        {flip ? card.back_text : card.front_text}
      </div>
    </button>
  );
}

// ============================================================
// LEARNER — WEAK TOPICS + RECOMMENDATIONS
// ============================================================

export function LearnerWeakTopicsPage() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/learner/ai/weak-topics");
        setRows(data.data?.weak_topics || []);
      } catch (err) { toast.error(formatApiError(err)); setRows([]); }
    })();
  }, []);
  if (rows === null) return <DashboardLayout title="My Weak Topics"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="My Weak Topics" breadcrumbs={["My Learning", "Weak Topics"]}>
      <div className="space-y-3" data-testid="ai-weak-topics">
        <div className="flex items-center gap-2">
          <AIBadge />
          <div className="text-xs text-slate-500">Detected from your recent exam attempts and pending training.</div>
        </div>
        {rows.length === 0 ? (
          <EmptyState title="No weak topics yet" description="Complete exams and courses so AI can identify areas to improve." icon={TrendingUp} />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {rows.map((r, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`weak-topic-${i}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm font-bold text-slate-800">{r.topic}</div>
                    <div className="text-[11px] text-slate-500">{r.course_title || ""}</div>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                    r.risk_level === "high" || r.risk_level === "critical" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"
                  }`}>{r.risk_level}</span>
                </div>
                <div className="mt-2 text-xs text-slate-600">{r.reason}</div>
                <div className="mt-2 text-xs text-[#329112] font-medium">{r.recommended_action}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export function LearnerRecommendationsPage() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/learner/ai/recommendations");
        setRows(data.data?.recommendations || []);
      } catch (err) { toast.error(formatApiError(err)); setRows([]); }
    })();
  }, []);
  if (rows === null) return <DashboardLayout title="My Recommendations"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="AI Recommendations" breadcrumbs={["My Learning", "Recommendations"]}>
      <div className="space-y-3" data-testid="ai-recommendations">
        <div className="flex items-center gap-2"><AIBadge /><div className="text-xs text-slate-500">Personalized for you.</div></div>
        {rows.length === 0 ? (
          <EmptyState title="No recommendations" description="You're all caught up! Check back later." icon={Lightbulb} />
        ) : (
          rows.map((r) => (
            <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`rec-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-bold text-slate-800">{r.recommendation_title}</div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                  r.priority === "critical" ? "bg-rose-100 text-rose-700" :
                  r.priority === "high" ? "bg-amber-100 text-amber-700" :
                  "bg-slate-100 text-slate-600"
                }`}>{r.priority}</span>
              </div>
              <div className="mt-1 text-xs text-slate-600">{r.recommendation_text}</div>
              <div className="mt-1 text-[10px] text-slate-400">Reason: {r.reason}</div>
            </div>
          ))
        )}
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// LEARNER — CHAT HISTORY
// ============================================================

export function LearnerAIChatHistoryPage() {
  const [sessions, setSessions] = useState(null);
  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const { data } = await api.get("/learner/ai/chat-sessions");
      setSessions(data.data?.sessions || []);
    } catch (err) { toast.error(formatApiError(err)); setSessions([]); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this chat?")) return;
    try {
      await api.delete(`/learner/ai/chat-sessions/${id}`);
      toast.success("Deleted"); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  if (sessions === null) return <DashboardLayout title="AI Chat History"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="AI Chat History" breadcrumbs={["My Learning", "AI Chats"]}>
      <div className="space-y-2" data-testid="ai-chat-history">
        {sessions.length === 0 ? (
          <EmptyState title="No chats yet" description="Start a conversation with the AI Study Assistant." icon={MessageSquare} />
        ) : sessions.map((s) => (
          <div key={s.id} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center justify-between" data-testid={`chat-session-${s.id}`}>
            <div>
              <div className="text-sm font-semibold text-slate-800">{s.session_title || "Untitled"}</div>
              <div className="text-[11px] text-slate-500">{s.ai_feature} · {new Date(s.updated_at).toLocaleString()}</div>
            </div>
            <button type="button" onClick={() => del(s.id)} data-testid={`chat-delete-${s.id}`}
                    className="text-rose-600 hover:text-rose-700"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// FACULTY — AI QUESTION GENERATOR
// ============================================================

export function FacultyAIQuestionGeneratorPage() {
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [count, setCount] = useState(5);
  const [difficulty, setDifficulty] = useState("medium");
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [saved, setSaved] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/faculty/my-courses");
        setCourses(data.data?.courses || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const generate = async () => {
    if (!courseId) return toast.error("Select a course");
    setLoading(true); setItems([]);
    try {
      const { data } = await api.post("/faculty/ai/generate-questions", {
        course_id: courseId, count, difficulty, question_type: "mcq", topic,
      });
      setItems(data.data?.questions || []);
      toast.success(`Generated ${data.data?.questions?.length || 0} questions`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const saveOne = async (qid) => {
    try {
      await api.post(`/faculty/ai/generated-questions/${qid}/save-to-question-bank-draft`);
      setSaved({ ...saved, [qid]: true });
      toast.success("Saved to question bank as pending review");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardLayout title="AI Question Generator" breadcrumbs={["Faculty", "AI Question Generator"]}>
      <div className="space-y-4" data-testid="faculty-ai-question-gen">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 grid gap-3 sm:grid-cols-4 items-end">
          <div className="sm:col-span-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Course</label>
            <Select value={courseId} onValueChange={setCourseId}>
              <SelectTrigger data-testid="fq-course"><SelectValue placeholder="Pick a course" /></SelectTrigger>
              <SelectContent>
                {courses.map((c) => (<SelectItem key={c.id} value={c.id}>{c.course_title}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Count</label>
            <input type="number" min={3} max={20} value={count} onChange={(e) => setCount(parseInt(e.target.value) || 5)}
                   data-testid="fq-count"
                   className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Difficulty</label>
            <Select value={difficulty} onValueChange={setDifficulty}>
              <SelectTrigger data-testid="fq-difficulty"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="easy">Easy</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-3">
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Topic (optional)</label>
            <input type="text" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Hand hygiene"
                   data-testid="fq-topic"
                   className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <button
            type="button" onClick={generate} disabled={loading || !courseId} data-testid="fq-generate"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#101876] to-[#329112] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate
          </button>
        </div>

        {loading && <LoadingBlock />}
        {items.length > 0 && (
          <div className="space-y-3">
            <div className="text-xs text-slate-500 italic">Review each question before saving to the question bank. AI-generated questions must be human-reviewed.</div>
            {items.map((q, i) => (
              <div key={q.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`fq-item-${i}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-800">Q{i + 1}. {q.question_text}</div>
                    <ul className="mt-2 space-y-1 text-sm">
                      {(q.options_json || []).map((o, oi) => (
                        <li key={oi} className={`rounded px-2 py-1 ${q.correct_answer_json === oi ? "bg-green-50 text-green-800" : "text-slate-700"}`}>
                          {String.fromCharCode(65 + oi)}. {o} {q.correct_answer_json === oi && <span className="ml-1 text-[10px] font-bold text-[#329112]">✓ Correct</span>}
                        </li>
                      ))}
                    </ul>
                    {q.explanation && <div className="mt-2 rounded-lg bg-slate-50 p-2 text-xs text-slate-600"><strong>Explanation:</strong> {q.explanation}</div>}
                    <div className="mt-1 text-[10px] text-slate-400">Difficulty: {q.difficulty_level} · Topic: {q.topic || "—"} · AI confidence: {Math.round((q.ai_confidence_placeholder || 0) * 100)}%</div>
                  </div>
                  <button
                    type="button" onClick={() => saveOne(q.id)} disabled={saved[q.id]}
                    data-testid={`fq-save-${q.id}`}
                    className="inline-flex items-center gap-1 rounded-lg bg-[#101876] px-3 py-2 text-xs font-semibold text-white disabled:bg-slate-300"
                  >
                    {saved[q.id] ? <><CircleCheckBig className="h-3.5 w-3.5" /> Saved</> : <><ClipboardList className="h-3.5 w-3.5" /> Save to bank</>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// FACULTY — AI ASSISTANT CHAT
// ============================================================

export function FacultyAIAssistantPage() {
  const chat = useChat("/faculty/ai/assistant/chat");
  return (
    <DashboardLayout title="Faculty AI Assistant" breadcrumbs={["Faculty", "AI Assistant"]}>
      <div className="rounded-2xl border border-slate-200 bg-slate-50/60 flex flex-col h-[70vh]" data-testid="faculty-ai-chat">
        <div ref={chat.boxRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {chat.messages.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-8">
              <Bot className="mx-auto h-8 w-8 mb-2 text-[#101876]" />
              Try: <em>&ldquo;Generate a lesson outline for Fire Safety&rdquo;</em>
            </div>
          )}
          {chat.messages.map((m, i) => (<ChatBubble key={i} role={m.role} text={m.text} />))}
          {chat.sending && (<div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> AI is thinking…</div>)}
        </div>
        <div className="border-t border-slate-200 bg-white p-3 flex gap-2">
          <input type="text" value={chat.input} onChange={(e) => chat.setInput(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); chat.send(); } }}
                 placeholder="Ask the AI…" data-testid="faculty-ai-input"
                 className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#101876]/30" />
          <button type="button" onClick={chat.send} disabled={chat.sending} data-testid="faculty-ai-send"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            <Send className="h-4 w-4" /> Send
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// SUPER ADMIN — AI DASHBOARD
// ============================================================

export function AdminAIDashboardPage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data: r } = await api.get("/admin/ai/dashboard");
        setData(r.data);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  if (!data) return <DashboardLayout title="AI Dashboard"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="AI Dashboard" breadcrumbs={["Super Admin", "AI Dashboard"]}>
      <div className="space-y-4" data-testid="admin-ai-dashboard">
        <div className="flex items-center gap-2"><AIBadge /><div className="text-xs text-slate-500">Platform-wide AI usage.</div></div>
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total requests" value={data.total_requests} icon="Sparkles" tone="navy" testId="ai-kpi-total" index={0} />
          <StatCard label="Monthly requests" value={data.monthly_requests} icon="TrendingUp" tone="green" testId="ai-kpi-monthly" index={1} />
          <StatCard label="Active users" value={data.active_users} icon="Users" tone="slate" testId="ai-kpi-users" index={2} />
          <StatCard label="Failed" value={data.failed_requests} icon="AlertTriangle" tone="rose" testId="ai-kpi-failed" index={3} />
          <StatCard label="Total tokens" value={data.total_tokens.toLocaleString()} icon="Activity" tone="navy" testId="ai-kpi-tokens" index={4} />
          <StatCard label="Est. cost" value={`$${data.estimated_cost_placeholder}`} icon="Award" tone="green" testId="ai-kpi-cost" index={5} />
          <StatCard label="Feedback" value={data.feedback_total} icon="MessageSquare" tone="slate" testId="ai-kpi-feedback" index={6} />
          <StatCard label="Helpful %" value={`${data.helpful_pct}%`} icon="ThumbsUp" tone="green" testId="ai-kpi-helpful" index={7} />
        </section>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">Usage by feature</div>
          {data.by_feature.length === 0 ? (
            <EmptyState title="No usage yet" description="Ask a question in the AI Study Assistant to see data." icon={Sparkles} />
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-slate-400">
                <tr><th className="text-left py-2">Feature</th><th className="text-right">Requests</th><th className="text-right">Tokens</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.by_feature.map((r) => (
                  <tr key={r.feature}><td className="py-2 text-slate-700">{r.feature}</td><td className="text-right">{r.requests}</td><td className="text-right">{r.tokens?.toLocaleString()}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">Usage by organization</div>
          {data.by_organization.length === 0 ? (
            <EmptyState title="No org usage yet" description="AI usage will appear here." icon={Sparkles} />
          ) : (
            <table className="w-full text-sm">
              <tbody className="divide-y divide-slate-100">
                {data.by_organization.map((r) => (
                  <tr key={r.organization_id}><td className="py-2">{r.organization_name}</td><td className="text-right">{r.requests}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// SUPER ADMIN — PROVIDER + PROMPT + SETTINGS + USAGE LOGS + GEN QUESTIONS
// ============================================================

export function AdminAIProvidersPage() {
  const [rows, setRows] = useState(null);
  const [testResult, setTestResult] = useState(null);
  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const { data } = await api.get("/admin/ai/providers"); setRows(data.data?.providers || []); }
    catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  const test = async (id) => {
    setTestResult({ id, testing: true });
    try {
      const { data } = await api.post(`/admin/ai/providers/${id}/test`);
      setTestResult({ id, ...data.data });
      toast.success("Provider test complete");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  if (rows === null) return <DashboardLayout title="AI Providers"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="AI Provider Settings" breadcrumbs={["Super Admin", "AI Providers"]}>
      <div className="space-y-3" data-testid="admin-ai-providers">
        <div className="text-xs text-slate-500 italic">API keys are stored server-side via EMERGENT_LLM_KEY — never exposed to the frontend.</div>
        {rows.length === 0 ? (
          <EmptyState title="No providers" description="Add an AI provider to enable features." icon={Bot} />
        ) : rows.map((p) => (
          <div key={p.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`provider-${p.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-bold text-slate-800">{p.provider_name}
                  {p.is_default && <span className="ml-2 rounded-full bg-[#329112]/10 px-2 py-0.5 text-[10px] font-bold text-[#329112]">DEFAULT</span>}
                </div>
                <div className="text-[11px] text-slate-500 font-mono">{p.provider_code} · {p.model_name}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${p.status === "active" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"}`}>{p.status}</span>
                <button type="button" onClick={() => test(p.id)} data-testid={`provider-test-${p.id}`}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50">Test</button>
              </div>
            </div>
            {testResult?.id === p.id && !testResult.testing && (
              <div className="mt-2 rounded-lg bg-slate-50 p-2 text-xs">
                <strong>Response:</strong> {testResult.response || testResult.error} {testResult.tokens && <span className="text-slate-400">({testResult.tokens} tokens)</span>}
              </div>
            )}
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

export function AdminAIPromptTemplatesPage() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/ai/prompt-templates"); setRows(data.data?.templates || []); }
      catch (err) { toast.error(formatApiError(err)); setRows([]); }
    })();
  }, []);
  if (rows === null) return <DashboardLayout title="Prompt Templates"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="AI Prompt Templates" breadcrumbs={["Super Admin", "Prompt Templates"]}>
      <div className="grid gap-3 lg:grid-cols-2" data-testid="admin-prompt-templates">
        {rows.map((t) => (
          <div key={t.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`prompt-tpl-${t.prompt_code}`}>
            <div className="flex items-center justify-between">
              <div className="text-sm font-bold text-slate-800">{t.prompt_name}</div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${t.status === "active" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"}`}>{t.status}</span>
            </div>
            <div className="mt-1 font-mono text-[10px] text-slate-400">{t.prompt_code} v{t.version}</div>
            <div className="mt-2 rounded-lg bg-slate-50 p-2 text-[11px] text-slate-600 line-clamp-3">{t.system_prompt}</div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

export function AdminAIFeatureSettingsPage() {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/ai/settings"); setRows(data.data?.settings || []); }
      catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  const toggle = (code) => setRows(rows.map((r) => (r.feature_code === code ? { ...r, enabled: !r.enabled } : r)));
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/ai/settings", { settings: rows }); toast.success("Saved"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <DashboardLayout title="AI Feature Settings" breadcrumbs={["Super Admin", "AI Features"]}>
      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2" data-testid="admin-ai-feature-settings">
        {rows.map((s) => (
          <label key={s.feature_code} className="flex items-center justify-between rounded-lg p-2 hover:bg-slate-50 cursor-pointer" data-testid={`feature-${s.feature_code}`}>
            <span className="text-sm text-slate-800 capitalize">{s.feature_code.replace(/_/g, " ")}</span>
            <input type="checkbox" checked={!!s.enabled} onChange={() => toggle(s.feature_code)}
                   className="h-4 w-4 rounded border-slate-300 text-[#101876] focus:ring-[#101876]" />
          </label>
        ))}
        <button type="button" onClick={save} disabled={saving} data-testid="admin-ai-settings-save"
                className="mt-3 rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </DashboardLayout>
  );
}

export function AdminAIUsageLogsPage() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/ai/usage-logs"); setRows(data.data?.logs || []); }
      catch (err) { toast.error(formatApiError(err)); setRows([]); }
    })();
  }, []);
  if (rows === null) return <DashboardLayout title="AI Usage Logs"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="AI Usage Logs" breadcrumbs={["Super Admin", "Usage Logs"]}>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="admin-ai-usage-logs">
        {rows.length === 0 ? (
          <EmptyState title="No logs yet" description="AI calls will appear here." icon={ClipboardList} />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr><th className="p-3 text-left">Time</th><th className="p-3 text-left">Feature</th><th className="p-3 text-left">Model</th><th className="p-3 text-right">Tokens</th><th className="p-3">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`usage-row-${r.id}`}>
                  <td className="p-3 text-[11px] text-slate-500">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="p-3">{r.ai_feature}</td>
                  <td className="p-3 font-mono text-[10px]">{r.provider_code}/{r.model_name}</td>
                  <td className="p-3 text-right">{r.total_tokens}</td>
                  <td className="p-3 text-center"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${r.request_status === "success" ? "bg-green-100 text-green-800" : "bg-rose-100 text-rose-800"}`}>{r.request_status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </DashboardLayout>
  );
}

export function AdminAIGeneratedQuestionsPage() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/ai/generated-questions"); setRows(data.data?.questions || []); }
      catch (err) { toast.error(formatApiError(err)); setRows([]); }
    })();
  }, []);
  if (rows === null) return <DashboardLayout title="AI Generated Questions"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="AI Generated Questions Review" breadcrumbs={["Super Admin", "AI Questions"]}>
      <div className="space-y-2" data-testid="admin-ai-gen-questions">
        {rows.length === 0 ? (
          <EmptyState title="No AI questions" description="Faculty-generated AI questions will appear here." icon={Wand2} />
        ) : rows.map((q) => (
          <div key={q.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`gen-q-${q.id}`}>
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">{q.question_text}</div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                q.review_status === "approved" ? "bg-green-100 text-green-800" :
                q.review_status === "pending_approval" ? "bg-amber-100 text-amber-800" :
                "bg-slate-100 text-slate-600"
              }`}>{q.review_status}</span>
            </div>
            <div className="mt-1 text-[10px] text-slate-400">Generated by {q.generated_by_name || q.generated_by} · {new Date(q.created_at).toLocaleDateString()}</div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// SUPER ADMIN + HOSPITAL ADMIN — ADMIN AI ASSISTANT + INSIGHTS
// ============================================================

export function AdminAIAssistantPage() {
  const chat = useChat("/admin/ai/admin-assistant/chat");
  return (
    <DashboardLayout title="Admin AI Assistant" breadcrumbs={["Super Admin", "AI Assistant"]}>
      <div className="rounded-2xl border border-slate-200 bg-slate-50/60 flex flex-col h-[70vh]" data-testid="admin-ai-chat">
        <div ref={chat.boxRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {chat.messages.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-8">
              <Bot className="mx-auto h-8 w-8 mb-2 text-[#101876]" />
              Try: <em>&ldquo;Which organization has lowest compliance?&rdquo;</em>
            </div>
          )}
          {chat.messages.map((m, i) => (<ChatBubble key={i} role={m.role} text={m.text} />))}
          {chat.sending && (<div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> AI is thinking…</div>)}
        </div>
        <div className="border-t border-slate-200 bg-white p-3 flex gap-2">
          <input type="text" value={chat.input} onChange={(e) => chat.setInput(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); chat.send(); } }}
                 placeholder="Ask the admin AI…" data-testid="admin-ai-input"
                 className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#101876]/30" />
          <button type="button" onClick={chat.send} disabled={chat.sending} data-testid="admin-ai-send"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            <Send className="h-4 w-4" /> Send
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}

export function OrgAIAssistantPage() {
  const chat = useChat("/org/ai/admin-assistant/chat");
  return (
    <DashboardLayout title="Hospital AI Assistant" breadcrumbs={["Hospital Admin", "AI Assistant"]}>
      <div className="rounded-2xl border border-slate-200 bg-slate-50/60 flex flex-col h-[70vh]" data-testid="org-ai-chat">
        <div ref={chat.boxRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {chat.messages.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-8">
              <Bot className="mx-auto h-8 w-8 mb-2 text-[#101876]" />
              Try: <em>&ldquo;Which department has lowest compliance?&rdquo;</em>
            </div>
          )}
          {chat.messages.map((m, i) => (<ChatBubble key={i} role={m.role} text={m.text} />))}
          {chat.sending && (<div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> AI is thinking…</div>)}
        </div>
        <div className="border-t border-slate-200 bg-white p-3 flex gap-2">
          <input type="text" value={chat.input} onChange={(e) => chat.setInput(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); chat.send(); } }}
                 placeholder="Ask about your hospital…" data-testid="org-ai-input"
                 className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#101876]/30" />
          <button type="button" onClick={chat.send} disabled={chat.sending} data-testid="org-ai-send"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            <Send className="h-4 w-4" /> Send
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}

export function OrgAIComplianceInsightsPage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data: r } = await api.get("/org/ai/compliance-insights"); setData(r.data); }
      catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  if (!data) return <DashboardLayout title="Compliance Insights"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="AI Compliance Insights" breadcrumbs={["Hospital Admin", "AI Insights"]}>
      <div className="space-y-4" data-testid="org-ai-compliance">
        <div className="rounded-2xl border-2 border-[#329112] bg-gradient-to-br from-white to-green-50 p-5">
          <div className="flex items-center gap-2"><AIBadge /><div className="text-xs text-slate-500">AI-generated insight</div></div>
          <div className="mt-3 text-sm leading-relaxed text-slate-700">{data.insight_summary}</div>
        </div>
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Overall compliance" value={`${data.overall_compliance_pct}%`} icon="ShieldCheck" tone="navy" testId="ci-kpi-overall" index={0} />
          <StatCard label="Mandatory assigned" value={data.mandatory_assigned} icon="ClipboardList" tone="slate" testId="ci-kpi-assigned" index={1} />
          <StatCard label="Mandatory completed" value={data.mandatory_completed} icon="CircleCheckBig" tone="green" testId="ci-kpi-completed" index={2} />
          <StatCard label="Certs expiring 90d" value={data.certificates_expiring_90d} icon="Clock" tone="amber" testId="ci-kpi-expiring" index={3} />
        </section>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">Department compliance</div>
          {data.department_compliance.length === 0 ? (
            <EmptyState title="No department data" description="Add departments and assignments." icon={ShieldCheck} />
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-slate-400">
                <tr><th className="text-left py-2">Department</th><th className="text-right">Compliance %</th><th className="text-right">Completed / Assigned</th><th className="text-center">Risk</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.department_compliance.map((d) => (
                  <tr key={d.department_name}>
                    <td className="py-2">{d.department_name}</td>
                    <td className="text-right font-semibold">{d.compliance_pct}%</td>
                    <td className="text-right text-xs text-slate-500">{d.completed} / {d.assigned}</td>
                    <td className="text-center"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                      d.risk_level === "critical" ? "bg-rose-100 text-rose-700" :
                      d.risk_level === "high" ? "bg-amber-100 text-amber-700" :
                      d.risk_level === "medium" ? "bg-slate-100 text-slate-600" :
                      "bg-green-100 text-green-800"
                    }`}>{d.risk_level}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export function OrgAIPredictiveRiskPage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data: r } = await api.get("/org/ai/predictive-risk"); setData(r.data); }
      catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  if (!data) return <DashboardLayout title="Predictive Risk"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="AI Predictive Risk" breadcrumbs={["Hospital Admin", "Predictive Risk"]}>
      <div className="space-y-4" data-testid="org-ai-predictive">
        <div className="flex items-center gap-2"><AIBadge /><div className="text-xs text-slate-500">Rule-based scoring with AI-generated explanations.</div></div>
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard label="At-risk learners" value={data.at_risk_count} icon="AlertTriangle" tone="rose" testId="pr-kpi-atrisk" index={0} />
          <StatCard label="Certs may lapse (30d)" value={data.certificates_lapse_30d} icon="Clock" tone="amber" testId="pr-kpi-lapse" index={1} />
          <StatCard label="Report generated" value={new Date(data.generated_at).toLocaleDateString()} icon="Activity" tone="slate" testId="pr-kpi-generated" index={2} />
        </section>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">Learners at risk</div>
          {data.at_risk_learners.length === 0 ? (
            <EmptyState title="No at-risk learners" description="All learners are on track." icon={CircleCheckBig} />
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-slate-400">
                <tr><th className="text-left py-2">Learner</th><th>Course</th><th className="text-right">Risk</th><th>Reason</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.at_risk_learners.map((r, i) => (
                  <tr key={i}>
                    <td className="py-2">{r.user_name}<div className="text-[10px] text-slate-500">{r.user_email}</div></td>
                    <td className="py-2">{r.course_title}</td>
                    <td className="text-right">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                        r.risk_level === "critical" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"
                      }`}>{r.risk_level} {r.risk_score}</span>
                    </td>
                    <td className="py-2 text-xs text-slate-500">{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
